/**
 * The sensor tree under one place: place → (its own sensors, its child places)
 * → sensor → tag.
 *
 * Pure, so the browser, the review pane and the chart's own signal list are
 * three renderings of one structure rather than three groupings that can
 * disagree about where a tag lives.
 *
 * ── Where the shape comes from ───────────────────────────────────────────
 * A sensor is attached to exactly ONE of plant/line/machine (the API's
 * `ExactlyOneParentMixin`), so its own ids say which node it hangs off. They do
 * not say which line a machine sits on — `SensorOut` carries `machine_id` alone
 * — so the child places are passed in from the hierarchy collections, which do.
 * The hierarchy is plant → line → machine and stops there; machines do not nest.
 *
 * ── Only branches that hold something ────────────────────────────────────
 * A line with no sensors anywhere beneath it is pruned. The browser exists to
 * reach tags, and forty empty machines between the reader and the three that
 * are instrumented is a list nobody completes.
 *
 * ── A sensor whose place is not in the passed hierarchy is still shown ────
 * Under a node with no name, which the screen labels as an unknown place. It
 * happens when a child place is out of the reader's hierarchy grant but its
 * sensor is inside their sensor grant — and dropping the sensor would make a
 * tag the server will accept impossible to choose.
 */

const keyOf = (tier, id) => `${tier}:${id}`

/** Which node a sensor hangs off, from its own ids. */
const placeOfSensor = (sensor) =>
  sensor.machineId
    ? keyOf('asset', sensor.machineId)
    : sensor.lineId
      ? keyOf('line', sensor.lineId)
      : sensor.plantId
        ? keyOf('site', sensor.plantId)
        : null

const byName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true })

const toSensorNode = (sensor) => ({
  id: sensor.id,
  name: sensor.name,
  description: sensor.description,
  tags: sensor.tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
    description: tag.description,
    valueType: tag.valueType,
    valueUnitId: tag.valueUnitId,
  })),
})

/**
 * @param {object} input
 * @param {{tier: string, id: string, name: ?string}} input.place
 * @param {Array} input.sensors   `features/sensors` shape, every one at or beneath `place`.
 * @param {Array} [input.lines]    The place's lines, when it is a site.
 * @param {Array} [input.machines] Machines beneath the place, each with `parentId` = its line.
 */
export function buildSensorTree({ place, sensors, lines = [], machines = [] }) {
  const atPlace = new Map()
  const seen = new Set()
  for (const sensor of sensors) {
    if (seen.has(sensor.id)) continue
    seen.add(sensor.id)
    const key = placeOfSensor(sensor)
    if (!key) continue
    if (!atPlace.has(key)) atPlace.set(key, [])
    atPlace.get(key).push(toSensorNode(sensor))
  }

  const consumed = new Set()
  const node = (tier, id, name, code, children = []) => {
    const key = keyOf(tier, id)
    consumed.add(key)
    const own = (atPlace.get(key) ?? []).sort(byName)
    const kept = children.filter((child) => child.sensorCount > 0)
    return {
      key,
      tier,
      id,
      name,
      code: code ?? null,
      sensors: own,
      children: kept,
      // Sensors, not tags, decide what is pruned: a sensor with no tags is still
      // something the reader should see is there, with nothing to plot.
      sensorCount: own.length + kept.reduce((sum, child) => sum + child.sensorCount, 0),
      tagCount:
        own.reduce((sum, sensor) => sum + sensor.tags.length, 0) +
        kept.reduce((sum, child) => sum + child.tagCount, 0),
    }
  }

  const machineNode = (machine) => node('asset', machine.id, machine.name, machine.code)
  const lineNode = (line) =>
    node(
      'line',
      line.id,
      line.name,
      line.code,
      machines.filter((machine) => machine.parentId === line.id).map(machineNode),
    )

  let children = []
  if (place.tier === 'site') children = lines.map(lineNode)
  else if (place.tier === 'line') children = machines.filter((m) => m.parentId === place.id).map(machineNode)

  const root = node(place.tier, place.id, place.name, place.code, children)

  /* Anything left had a parent the hierarchy reads did not return. Shown, not
     dropped — see the note at the top. */
  for (const [key, list] of atPlace) {
    if (consumed.has(key)) continue
    const [tier, id] = key.split(':')
    const tagCount = list.reduce((sum, sensor) => sum + sensor.tags.length, 0)
    root.children.push({
      key, tier, id, name: null, code: null, sensors: list.sort(byName), children: [],
      sensorCount: list.length, tagCount,
    })
    root.sensorCount += list.length
    root.tagCount += tagCount
  }

  return root
}

/**
 * Tag id → where it lives: the tag, its sensor, and the places above it root
 * first. For labels, for the review pane, and for telling a chart's tags apart
 * from the ones the browser could not see.
 */
export function indexTags(tree) {
  const index = new Map()
  const walk = (node, trail) => {
    const here = [...trail, node]
    for (const sensor of node.sensors) {
      for (const tag of sensor.tags) index.set(tag.id, { tag, sensor, trail: here })
    }
    for (const child of node.children) walk(child, here)
  }
  if (tree) walk(tree, [])
  return index
}

/**
 * The same tree, holding only `tagIds`. Sensors with none of them and places
 * with nothing left beneath them are pruned, so the review pane reads as the
 * browser with everything unchosen taken away.
 */
export function pickTags(tree, tagIds) {
  if (!tree) return null
  const prune = (node) => {
    const sensors = node.sensors
      .map((sensor) => ({ ...sensor, tags: sensor.tags.filter((tag) => tagIds.has(tag.id)) }))
      .filter((sensor) => sensor.tags.length)
    const children = node.children.map(prune).filter(Boolean)
    const tagCount =
      sensors.reduce((sum, sensor) => sum + sensor.tags.length, 0) +
      children.reduce((sum, child) => sum + child.tagCount, 0)
    const sensorCount = sensors.length + children.reduce((sum, child) => sum + child.sensorCount, 0)
    return tagCount ? { ...node, sensors, children, sensorCount, tagCount } : null
  }
  return prune(tree)
}

/**
 * A tag's label: its name, else its description, else "Unnamed tag". `name` is
 * nullable on the sensor, and an invented label would be a claim about the tag.
 */
export const tagLabel = (tag, t) =>
  tag?.name || tag?.description || t('app:sensors.charts.tree.unnamedTag')
